/* ============================================================
   FERAL 2026 — Cart drawer interactions
   ------------------------------------------------------------
   Goal: cart feels instant, no race conditions, no glitches.

   How it works:
   1. ELF already debounces the change event 300ms before calling
      updateQuantity, so rapid +/- clicks coalesce into one call.
   2. Optimistic UI: we run BEFORE ELF (capture phase) and update
      line price / subtotal / checkout / remaining / progress fill
      synchronously, capped by the input's max attribute (which is
      now the lesser of quantity_rule.max and inventory_quantity).
   3. Single-flight: we patch CartItems.prototype.updateQuantity
      ONLY (CartDrawerItems extends CartItems and inherits — no
      double-wrap). If a request is already in flight, the new args
      are stored as queued and fired the moment the previous one
      completes via patched disableLoading.
   4. ELF's loading-overlay class triggers a small inline spinner
      next to the line price (not a full row dim).
   ============================================================ */
(function () {
  'use strict';

  /* ── Money formatter (mirrors Shopify's money / money_with_currency) ── */
  var SYMBOLS = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'CA$', NZD: 'NZ$' };
  function format(cents, currency, withCode) {
    var sym = SYMBOLS[currency] || (currency + ' ');
    var amount = Math.max(0, Math.round(cents)) / 100;
    return sym + amount.toFixed(2) + (withCode ? ' ' + currency : '');
  }

  /* ── Number flip-in animation (re-trigger by class swap + reflow) ── */
  function flip(el) {
    if (!el) return;
    el.classList.remove('feral-flip-in');
    el.offsetWidth; // eslint-disable-line no-unused-expressions
    el.classList.add('feral-flip-in');
  }

  /* ── Element finders ── */
  function $progress()  { return document.querySelector('[data-feral-progress]'); }
  function $fill()      { return document.querySelector('[data-feral-progress-fill]'); }
  function $total()     { return document.querySelector('[data-feral-money-total]'); }
  function $remaining() { return document.querySelector('[data-feral-money-remaining]'); }

  function syncUnlocked() {
    var fill = $fill(), progress = $progress();
    if (!fill || !progress) return;
    progress.classList.toggle('is-unlocked', parseFloat(fill.dataset.targetPct || '0') >= 100);
  }
  syncUnlocked();

  /* ── Optimistic UI on +/- click (capture phase, before ELF) ──
     ELF's stepUp already respects input.max. We mirror that check
     so the optimistic display never shows a value the input itself
     would refuse — preventing the "shows 6 then snaps to 5 with
     error" feeling. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.feral-cart__qty-btn');
    if (!btn) return;

    var qtyInput = btn.parentElement && btn.parentElement.querySelector('.feral-cart__qty-input');
    if (!qtyInput) return;

    var oldQty = parseInt(qtyInput.value, 10) || 0;
    var newQty = oldQty + (btn.name === 'plus' ? 1 : -1);
    if (newQty < 0) return;

    // Hard cap at input's max (= min(quantity_rule.max, inventory_quantity))
    if (qtyInput.max && newQty > parseInt(qtyInput.max, 10)) return;

    var row = btn.closest('.feral-cart__item');
    var priceEl = row && row.querySelector('.feral-cart__price');
    if (!row || !priceEl || !priceEl.dataset.cents) return;

    // LOCK the row's qty pill immediately — pointer-events: none + dim + spinner.
    // Release happens on disableLoading (or naturally when ELF replaces the HTML).
    row.classList.add('feral-cart__item--syncing');

    var currency = priceEl.dataset.currency || 'GBP';
    var oldLineCents = parseInt(priceEl.dataset.cents, 10);
    var unitCents = oldQty > 0 ? Math.round(oldLineCents / oldQty) : 0;
    var newLineCents = unitCents * newQty;
    var delta = newLineCents - oldLineCents;

    // Line price
    priceEl.dataset.cents = newLineCents.toString();
    if (newQty > 0) {
      priceEl.textContent = format(newLineCents, currency, false);
      flip(priceEl);
    }

    // Subtotal
    var totalEl = $total();
    if (totalEl && totalEl.dataset.cents) {
      var newTotal = parseInt(totalEl.dataset.cents, 10) + delta;
      totalEl.dataset.cents = newTotal.toString();
      totalEl.textContent = format(newTotal, currency, true);
      flip(totalEl);
    }

    // (Checkout button no longer shows the amount — "Secure checkout" + padlock)

    // Remaining + progress fill
    var progress = $progress();
    if (progress && totalEl && totalEl.dataset.cents) {
      var thresholdCents = parseInt(progress.dataset.thresholdCents || '0', 10);
      var subtotal = parseInt(totalEl.dataset.cents, 10);
      var newRemaining = Math.max(0, thresholdCents - subtotal);

      var remainingEl = $remaining();
      if (remainingEl) {
        remainingEl.dataset.cents = newRemaining.toString();
        remainingEl.textContent = format(newRemaining, currency, false);
        flip(remainingEl);
      }

      var newPct = subtotal >= thresholdCents ? 100 : (subtotal * 100 / thresholdCents);
      var fill = $fill();
      if (fill) {
        fill.dataset.targetPct = newPct.toString();
        fill.style.width = newPct + '%';
      }
      progress.classList.toggle('is-unlocked', subtotal >= thresholdCents);
    }
  }, true); // capture so we run before ELF's quantity-input change handler

  /* ── Lock the row when "Remove" is clicked too — same UX as +/- ── */
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('.feral-cart__remove');
    if (!rm) return;
    var row = rm.closest('.feral-cart__item');
    if (row) row.classList.add('feral-cart__item--syncing');
  }, true);

  /* ── Sync unlocked-state class after ELF re-renders the cart drawer HTML ── */
  var inner = document.querySelector('#CartDrawer');
  if (inner) {
    new MutationObserver(syncUnlocked).observe(inner, { childList: true, subtree: true });
  }

  /* ── Single-flight enforcement on ELF's updateQuantity ──
     CartDrawerItems extends CartItems and inherits updateQuantity —
     so we ONLY patch CartItems. Patching both would double-wrap and
     deadlock (the inner wrapper sees the outer's in-flight=true and
     queues forever). */
  var Cls = customElements.get('cart-items');
  if (Cls && Cls.prototype && typeof Cls.prototype.updateQuantity === 'function' && !Cls.prototype._feralPatched) {
    Cls.prototype._feralPatched = true;

    var origUpdate = Cls.prototype.updateQuantity;
    var origDisable = Cls.prototype.disableLoading;

    Cls.prototype.updateQuantity = function (line, quantity, eventName, variantId) {
      var self = this;
      // If a previous request is in flight, store latest args. Older
      // queued args are overwritten — we only ever care about the LAST one.
      if (self._feralInFlight) {
        self._feralQueuedArgs = [line, quantity, eventName, variantId];
        return;
      }
      self._feralInFlight = true;
      origUpdate.call(self, line, quantity, eventName, variantId);
    };

    Cls.prototype.disableLoading = function (line) {
      origDisable.call(this, line);
      var self = this;
      self._feralInFlight = false;

      // If we have a queued request, leave the row LOCKED — another AJAX
      // is about to fire. Only release the lock if the cart is fully settled.
      if (self._feralQueuedArgs) {
        var args = self._feralQueuedArgs;
        self._feralQueuedArgs = null;
        setTimeout(function () {
          if (self._feralInFlight) return;
          self._feralInFlight = true;
          origUpdate.apply(self, args);
        }, 16);
      } else {
        // No more pending — release any lingering syncing locks.
        // (HTML replacement usually destroys the locked row, but if for
        // any reason the row survived, this guarantees it unlocks.)
        document.querySelectorAll('.feral-cart__item--syncing').forEach(function (r) {
          r.classList.remove('feral-cart__item--syncing');
        });
      }
    };
  }
})();
